/**
 * Conversation Store
 * Multi-Chat System: Zustand store for UI preferences only
 * Note: Conversation ID is now managed via URL parameters, not Zustand
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { log } from '../utils/errors/logger';

interface ConversationState {
  // UI state - sidebar preferences
  conversationSidebarOpen: boolean;
  documentReferencesSidebarOpen: boolean;

  // Actions
  toggleConversationSidebar: () => void;
  setConversationSidebarOpen: (open: boolean) => void;
  toggleDocumentReferencesSidebar: () => void;
  setDocumentReferencesSidebarOpen: (open: boolean) => void;
}

export const useConversationStore = create<ConversationState>()(
  persist(
    (set, get) => ({
      // Initial state
      conversationSidebarOpen: false,
      documentReferencesSidebarOpen: false,

      // Actions
      toggleConversationSidebar: () => {
        const newState = !get().conversationSidebarOpen;
        log.user.action('sidebar_toggle', 'ConversationStore', {
          sidebar: 'conversations',
          open: newState
        });

        set({ conversationSidebarOpen: newState });
      },

      setConversationSidebarOpen: (open: boolean) => {
        if (get().conversationSidebarOpen !== open) {
          log.user.action('sidebar_set', 'ConversationStore', {
            sidebar: 'conversations',
            open
          });
        }

        set({ conversationSidebarOpen: open });
      },

      toggleDocumentReferencesSidebar: () => {
        const newState = !get().documentReferencesSidebarOpen;
        log.user.action('sidebar_toggle', 'ConversationStore', {
          sidebar: 'document_references',
          open: newState
        });

        set({ documentReferencesSidebarOpen: newState });
      },

      setDocumentReferencesSidebarOpen: (open: boolean) => {
        if (get().documentReferencesSidebarOpen !== open) {
          log.user.action('sidebar_set', 'ConversationStore', {
            sidebar: 'document_references',
            open
          });
        }

        set({ documentReferencesSidebarOpen: open });
      },
    }),
    {
      name: 'conversation-ui-preferences',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist UI preferences only
        conversationSidebarOpen: state.conversationSidebarOpen,
        documentReferencesSidebarOpen: state.documentReferencesSidebarOpen,
      }),
    }
  )
);